import type {
  CalendarEventType,
  FacultyLoadStatus,
  ImportBatchStatus,
  ImportRowStatus,
  LoadType,
  OnlinePlatform,
  ScheduledModality,
  ScheduleStatus,
} from '../enums';

export const SCHEDULE_EXCEPTION_TYPE_VALUES = ['CANCELLED', 'RESCHEDULED', 'ROOM_CHANGE', 'MODALITY_CHANGE'] as const;
export type ScheduleExceptionType = (typeof SCHEDULE_EXCEPTION_TYPE_VALUES)[number];

export interface FacultyLoadDto {
  id: string;
  facultyId: string;
  facultyName: string;
  employeeNumber: string;
  termId: string;
  termCode: string;
  academicYearCode: string;
  courseSectionId: string | null;
  courseCode: string | null;
  courseTitle: string | null;
  sectionCode: string | null;
  loadType: LoadType;
  units: number | null;
  contactHoursPerWeek: number | null;
  description: string | null;
  collegeId: string | null;
  departmentId: string | null;
  departmentName: string | null;
  status: FacultyLoadStatus;
  remarks: string | null;
  scheduleCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface FacultyLoadSummaryDto {
  facultyId: string;
  facultyName: string;
  employeeNumber: string;
  employmentTypeCode: string;
  departmentName: string | null;
  totalUnits: number;
  totalContactHours: number;
  byLoadType: Partial<Record<LoadType, { units: number; contactHours: number; count: number }>>;
}

export interface ScheduleDto {
  id: string;
  facultyId: string;
  facultyName: string;
  employeeNumber: string;
  facultyLoadId: string | null;
  courseSectionId: string;
  courseCode: string;
  courseTitle: string;
  sectionCode: string;
  academicYearId: string;
  academicYearCode: string;
  termId: string;
  termCode: string;
  collegeId: string | null;
  collegeName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  programId: string | null;
  programName: string | null;
  /** ISO 1 = Monday … 7 = Sunday. */
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  scheduledMinutes: number;
  crossesMidnight: boolean;
  locationId: string | null;
  locationCode: string | null;
  locationName: string | null;
  /** Campus / building / room of the location (§11.1). */
  campusName: string | null;
  buildingName: string | null;
  roomNumber: string | null;
  scheduledModality: ScheduledModality;
  loadType: LoadType;
  onlinePlatform: OnlinePlatform | null;
  meetingUrl: string | null;
  effectiveFrom: string;
  effectiveTo: string;
  status: ScheduleStatus;
  version: number;
  conflictOverride: boolean;
  conflictOverrideReason: string | null;
  importBatchId: string | null;
  parentScheduleId: string | null;
  remarks: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ScheduleConflictType = 'FACULTY' | 'ROOM' | 'SECTION';

export interface ScheduleConflictDto {
  type: ScheduleConflictType;
  /** BLOCKING conflicts require an acknowledged override; WARNINGs are informational. */
  severity: 'BLOCKING' | 'WARNING';
  scheduleId: string | null;
  conflictingScheduleId: string;
  overlapMinutes: number;
  message: string;
}

export interface ScheduleConflictPairDto {
  type: ScheduleConflictType;
  severity: 'BLOCKING' | 'WARNING';
  overlapMinutes: number;
  first: ScheduleDto;
  second: ScheduleDto;
}

export interface ScheduleExceptionDto {
  id: string;
  scheduleId: string;
  exceptionDate: string;
  exceptionType: ScheduleExceptionType;
  newStartTime: string | null;
  newEndTime: string | null;
  newLocationId: string | null;
  newLocationName: string | null;
  newModality: ScheduledModality | null;
  reason: string;
  revokedAt: string | null;
  createdAt: string;
}

export interface ScheduleOccurrenceDto {
  scheduleId: string;
  date: string;
  startTime: string;
  endTime: string;
  scheduledStart: string;
  scheduledEnd: string;
  scheduledMinutes: number;
  crossesMidnight: boolean;
  facultyId: string;
  facultyName: string;
  courseCode: string;
  courseTitle: string;
  sectionCode: string;
  locationId: string | null;
  locationName: string | null;
  scheduledModality: ScheduledModality;
  /** Modality in effect for this date (after exceptions). */
  modality: ScheduledModality;
  loadType: LoadType;
  onlinePlatform: OnlinePlatform | null;
  meetingUrl: string | null;
  cancelled: boolean;
  exceptionType: ScheduleExceptionType | null;
  exceptionReason: string | null;
  /** Holiday or suspension that cancels this meeting (§24); `cancelled` is then true. */
  calendarEvent: { id: string; eventType: CalendarEventType; title: string } | null;
}

export interface ImportIssue {
  column: string | null;
  message: string;
  severity: 'error' | 'warning';
}

export interface ImportRowDto {
  id: string;
  rowNumber: number;
  values: Record<string, string>;
  status: ImportRowStatus;
  issues: ImportIssue[];
  scheduleIds: string[];
}

export interface ImportBatchDto {
  id: string;
  fileName: string;
  fileFormat: 'CSV' | 'XLSX';
  status: ImportBatchStatus;
  totalRows: number;
  validRows: number;
  warningRows: number;
  errorRows: number;
  skippedRows: number;
  committedRows: number;
  schedulesCreated: number;
  uploadedBy: string;
  uploadedByEmail: string | null;
  uploadedAt: string;
  committedAt: string | null;
}
